/* eslint-disable complexity */
import React, { useState, useEffect, useCallback } from 'react';
import { wrapFunctionalComponent } from 'utils/framework';
import storeUtils from 'utils/Store';
import userLocation from 'utils/userLocation/UserLocation';
import scriptUtils from 'utils/LoadScripts';
import withInnerModal from 'components/withInnerModal/withInnerModal';
import FindInStoreMapModal from 'components/GlobalModals/FindInStore/FindInStoreMapModal/FindInStoreMapModal';
import ExperienceLocation from 'components/Happening/ExperienceLocation';
import Modal from 'components/Modal/Modal';
import profileApi from 'services/api/profile';
import basketUtils from 'utils/Basket';
import localeUtils from 'utils/LanguageLocale';
import StoreSelectorModalBindings from 'analytics/bindingMethods/components/globalModals/storeSelectorModal/StoreSelectorModalBindings';
import NoStores from 'components/SharedComponents/Stores/NoStores/NoStores';
import LOCATION from 'utils/userLocation/Constants';
import Loader from 'components/Loader/Loader';
import { PICKUP } from 'constants/UpperFunnel';
import { VARIANTS } from 'constants/InventoryAwareStorePicker';
import userUtils from 'utils/User';
import anaConsts from 'analytics/constants';
import processEvent from 'analytics/processEvent';
import {
    Box, Grid, Button, Divider
} from 'components/ui';
import cookieUtils from 'utils/Cookies';
import sdnApi from 'services/api/sdn';
import StoreList from 'components/GlobalModals/InventoryAwareStorePickerModal/StoreList';
import ShopYourStoreUtils from 'utils/ShopYourStore';
import ProductDetails from 'components/GlobalModals/InventoryAwareStorePickerModal/ProductDetails';
import { modal } from 'style/config';
import Empty from 'constants/empty';

const MODAL_HEIGHT = 600;
const LOADER_HEIGHT = MODAL_HEIGHT - 250;
const LEFT_COLUMN_WIDTH = 240;

function InventoryAwareStorePickerModal(props) {
    const {
        localization,
        showStoreDetails,
        onDismiss,
        options,
        showCancelButton,
        okButtonText,
        innerModal,
        openModal,
        entry,
        shouldTriggerPageLoadAnalytics = true,
        afterCallback = Empty.Function,
        callback = Empty.Function,
        mountCallback = Empty.Function,
        cancelCallback = Empty.Function,
        onChange,
        user,
        basket,
        preferredStoreInfo,
        dispatchUpdatePreferredStore,
        dispatchRefreshBasket,
        dispatchStoreChangedFromHeader,
        dispatchDraftStoreDetails,
        dispatchShowInfoModal,
        variant = VARIANTS.STORE,
        currentProduct
    } = props;

    const isProductVariant = variant === VARIANTS.PRODUCT;
    const [currentLocation, setCurrentLocation] = useState(null);
    const [isLoaded, setIsLoaded] = useState(false);
    const [storeList, setStoreList] = useState(null);
    const [showNoStoreResults, setShowNoStoreResults] = useState(false);
    const [selectedStore, setSelectedStore] = useState(null);
    const [countryMismatch, setCountryMismatch] = useState(false);
    const [isOpen, setIsOpen] = useState(true);
    const [isShowChangeAlertModal, setIsShowChangeAlertModal] = useState(false);
    const [pickupInventory, setPickupInventory] = useState(null);

    function getOkButtonText() {
        if (isProductVariant) {
            return localization.chooseThisStore;
        }

        if (okButtonText != null) {
            return okButtonText;
        }

        return (
            okButtonText ||
            (options?.isUpperFunnel ? (options.taskQueue ? localization.done : localization.showResults) : localization.chooseThisStore)
        );
    }

    function getModalTitleText() {
        if (isProductVariant) {
            return localization.chooseStore;
        }

        return localization[options?.isUpperFunnel ? 'pickupInStore' : 'changeStore'];
    }

    const loadGoogleScript = useCallback(onScriptLoad => {
        if (!window.google) {
            scriptUtils.loadScripts([scriptUtils.SCRIPTS.GOOGLE], onScriptLoad);
        } else {
            onScriptLoad();
        }
    }, []);

    const getUserLocation = useCallback(() => {
        userLocation.determineLocation(
            locationObj => {
                getStoresAndUpdateState(locationObj, true);
            },
            null,
            { sequence: userLocation.getDefaultStrategiesSequence() }
        );
    }, []);

    const getStoreLocation = useCallback(() => {
        const storeData = {
            display: storeUtils.getStoreDisplayName(preferredStoreInfo),
            country: preferredStoreInfo?.address?.country,
            zipCode: preferredStoreInfo?.address?.postalCode,
            lat: preferredStoreInfo?.latitude,
            lon: preferredStoreInfo?.longitude
        };
        getStoresAndUpdateState(storeData);
    }, [preferredStoreInfo]);

    const selectStore = useCallback(storeItem => {
        setSelectedStore(storeItem);
    }, []);

    const fireAnalytics = useCallback(() => {
        const placement = entry ? `:${entry}` : '';
        processEvent.process(anaConsts.LINK_TRACKING_EVENT, {
            data: {
                eventStrings: [anaConsts.Event.EVENT_71],
                linkName: anaConsts.LinkData.BOPIS_STORE_SELECTED,
                actionInfo: `change store${placement}:${storeUtils.removePrepAtStartForKohls(selectedStore.displayName)}`,
                storeId: selectedStore.storeId
            }
        });
    }, [entry, selectedStore]);

    const hasSwitchedToKohlsStore = useCallback(
        preferredStoreInfoId => {
            const userPreferredStoreId = preferredStoreInfoId ? preferredStoreInfoId : user.preferredStoreInfo.storeId;

            return userPreferredStoreId !== selectedStore.storeId && storeUtils.isKohlsStore(selectedStore);
        },
        [selectedStore, user]
    );

    const updateProfile = useCallback(
        (shouldTriggerCallback = true) => {
            onDismiss();

            return profileApi.switchPreferredStore(selectedStore.storeId, true).then(response => {
                storeUtils.cacheStoreData(Object.assign({}, selectedStore, { isUserSelected: true }));

                if (Sephora.configurationSettings.setZipStoreCookie) {
                    cookieUtils.write(cookieUtils.KEYS.PREFERRED_STORE, selectedStore.storeId, null, false, false);
                }

                Promise.resolve()
                    .then(() => {
                        const shouldClearCache = !userUtils.isAnonymous();
                        dispatchUpdatePreferredStore(selectedStore, shouldClearCache, true);

                        if (typeof props.onChange === 'function') {
                            onChange();
                        } else if (basket?.pickupBasket?.itemCount) {
                            dispatchRefreshBasket(true);
                        }

                        // Call the legacy callback prop (from ReserveAndPickUpModal)
                        callback(response?.success);

                        shouldTriggerCallback &&
                            afterCallback({
                                storeId: selectedStore.storeId
                            });

                        // We need to dispatch this action at the very end
                        // This is why this check is done here.
                        if (options?.isHeader) {
                            dispatchStoreChangedFromHeader(selectedStore);
                        }
                    })
                    .catch(error => {
                        // eslint-disable-next-line no-console
                        console.warn(error);
                    });

                return {
                    ...response,
                    preferredStoreInfoId: user.preferredStoreInfo.storeId
                };
            });
        },
        [
            selectedStore,
            onDismiss,
            afterCallback,
            callback,
            onChange,
            options,
            user,
            basket,
            dispatchUpdatePreferredStore,
            dispatchRefreshBasket,
            dispatchStoreChangedFromHeader
        ]
    );

    const showInfoModalCallback = useCallback(() => {
        updateProfile().then(response => {
            const { success } = response;

            if (success) {
                //Fire event for Sephora One Tag
                fireAnalytics();
            }
        });
    }, [updateProfile, fireAnalytics]);

    const confirmationCancelCallback = useCallback(() => {
        setIsShowChangeAlertModal(false);
    }, []);

    const upfunnelActions = useCallback(() => {
        const payload = {
            storeId: selectedStore?.storeId,
            displayName: selectedStore?.displayName
        };
        const taskId = PICKUP;
        const changeStorePromise = () =>
            new Promise(resolve => {
                dispatchUpdatePreferredStore(selectedStore, false, true);
                updateProfile(false);
                resolve();
            });
        options.taskQueue.add(changeStorePromise, taskId);
        dispatchDraftStoreDetails(payload);
        afterCallback({ storeId: selectedStore.storeId });
        onDismiss();
    }, [selectedStore, options, afterCallback, onDismiss, updateProfile, dispatchUpdatePreferredStore, dispatchDraftStoreDetails]);

    const showConfirmationModal = useCallback(
        (useUpfunnelAction = false) => {
            const cbFn = useUpfunnelAction ? upfunnelActions : showInfoModalCallback;
            const storeDisplayName = storeUtils.getStoreDisplayNameWithSephora(selectedStore);
            let infoMessage = `${localization.changeStoreMessage} <strong>${storeDisplayName}</strong> ${localization.changeStoreMessage2}.`;

            if (selectedStore.isRopisable) {
                infoMessage += `<br /><br />${localization.anyPromosRewardsMsg}`;
            }

            if (hasSwitchedToKohlsStore()) {
                if (options?.isHeader) {
                    infoMessage += `<br /><br />${localization.changeItemQtyMsgHeader}`;

                    if (basketUtils.hasBopisItemsCountGreaterThanOne()) {
                        infoMessage += `<strong> ${localization.changeItemQtyMsgHeaderMoreThanOne}</strong>`;
                    }
                } else {
                    infoMessage += `<br /><br />${localization.changeItemQtyMsg}`;
                }
            }

            setIsShowChangeAlertModal(true);
            dispatchShowInfoModal({
                isOpen: true,
                title: localization.changeStore,
                message: infoMessage,
                buttonText: localization.ok,
                showCancelButton: true,
                callback: cbFn,
                cancelCallback: confirmationCancelCallback,
                dataAtButton: 'change_store_ok_btn',
                dataAtCancelButton: 'change_store_cancel_btn',
                isHtml: true
            });
        },
        [
            selectedStore,
            localization,
            hasSwitchedToKohlsStore,
            options,
            upfunnelActions,
            showInfoModalCallback,
            confirmationCancelCallback,
            dispatchShowInfoModal
        ]
    );

    const changeStore = useCallback(() => {
        if (hasSwitchedToKohlsStore() || (basketUtils.hasPickupItems() && user?.preferredStoreInfo?.storeId !== selectedStore.storeId)) {
            showConfirmationModal();
        } else {
            updateProfile();
            //Fire event for Sephora One Tag
            fireAnalytics();
        }
    }, [hasSwitchedToKohlsStore, selectedStore, showConfirmationModal, updateProfile, fireAnalytics, user]);

    const handleChangeStore = useCallback(() => {
        if (options?.taskQueue) {
            if (hasSwitchedToKohlsStore() || (basketUtils.hasPickupItems() && user?.preferredStoreInfo?.storeId !== selectedStore.storeId)) {
                showConfirmationModal(true);
            } else {
                upfunnelActions();
            }
        } else {
            changeStore();
        }
    }, [options, hasSwitchedToKohlsStore, selectedStore, showConfirmationModal, upfunnelActions, changeStore, user]);

    const close = useCallback(() => {
        // Call cancelCallback if provided (e.g., to re-show change method modal)
        cancelCallback();
        onDismiss();
        setIsOpen(false);
    }, [onDismiss, cancelCallback]);

    const getStoreListCountry = useCallback(stores => {
        return stores && stores.length > 0 && stores[0].address.country;
    }, []);

    const isCountryMismatch = useCallback(country => {
        return country !== localeUtils.getCurrentCountry();
    }, []);

    const getStoresAndUpdateState = useCallback(
        async locationObj => {
            if (locationObj?.display != null) {
                setCurrentLocation(locationObj.display);
            } else {
                getUserLocation();
            }

            if (
                (locationObj.src === LOCATION.TYPES.GEOLOCATION || locationObj.src === LOCATION.TYPES.PREDICTION) &&
                typeof locationObj.country === 'undefined'
            ) {
                locationObj.country = localeUtils.getCurrentCountry();
            }

            if (locationObj.country === localeUtils.getCurrentCountry().toUpperCase()) {
                try {
                    const sku = isProductVariant && currentProduct ? currentProduct.currentSku : null;
                    const skuId = sku?.skuId;
                    const payload = {
                        latitude: locationObj.lat,
                        longitude: locationObj.lon,
                        biAccountId: user?.beautyInsiderAccount?.biAccountId || user?.profileId,
                        ...(skuId && { skuId })
                    };

                    const response = await sdnApi.getStores(payload);
                    const stores = response?.stores || [];

                    const storesCountry = getStoreListCountry(stores);
                    const isCountryMismatchResult = isCountryMismatch(storesCountry);
                    userLocation.setNewLocation(locationObj);
                    const selected = stores.find(storeItem => storeItem.storeId === preferredStoreInfo.storeId);
                    // Build pickup inventory
                    const pickupInventoryData = ShopYourStoreUtils.buildPickupInventory({
                        stores,
                        pickupItems: response?.pickupItems,
                        inventory: response?.inventory,
                        ...(skuId && { currentSkuId: skuId })
                    });

                    setPickupInventory(pickupInventoryData);
                    setStoreList(isCountryMismatchResult ? null : stores);
                    setIsLoaded(true);
                    setShowNoStoreResults(isCountryMismatchResult);
                    setCountryMismatch(isCountryMismatchResult);
                    setSelectedStore(showStoreDetails && selected ? selected : storeUtils.isStoreTypeKohls(stores?.[0]) ? selected : stores[0]);
                } catch (error) {
                    setIsLoaded(true);
                    setShowNoStoreResults(true);
                    setCountryMismatch(false);
                    setStoreList([]);
                }
            } else {
                setIsLoaded(true);
                setShowNoStoreResults(true);
                setStoreList([]);
                setCountryMismatch(true);
            }
        },
        [showStoreDetails, getUserLocation, getStoreListCountry, isCountryMismatch, preferredStoreInfo, user, isProductVariant, currentProduct]
    );

    useEffect(() => {
        // Call mountCallback if provided (e.g., to hide change method modal)
        mountCallback();

        const getLocationFunction = !options?.isUpperFunnel && showStoreDetails ? getStoreLocation : getUserLocation;

        loadGoogleScript(getLocationFunction);

        if (shouldTriggerPageLoadAnalytics) {
            StoreSelectorModalBindings.storeSwitcherLoad(entry);
        }
    }, []);

    const modalTitle = getModalTitleText();
    const footerColumns = showCancelButton || options?.isUpperFunnel ? 2 : 1;

    return !isShowChangeAlertModal ? (
        <Modal
            isOpen={isOpen}
            width={isProductVariant ? 680 : 400}
            onDismiss={close}
            hasBodyScroll={true}
        >
            {innerModal}
            <Modal.Header>
                <Modal.Title children={modalTitle} />
            </Modal.Header>
            <Modal.Body css={isProductVariant ? { padding: '0 !important' } : undefined}>
                <Grid
                    gap={0}
                    columns={isProductVariant ? [1, `${LEFT_COLUMN_WIDTH}px auto`] : 1}
                >
                    {isProductVariant && (
                        <Box
                            borderColor='divider'
                            borderRightWidth={[null, 1]}
                            padding={5}
                            display={['none', 'block']}
                        >
                            <ProductDetails product={currentProduct} />
                        </Box>
                    )}
                    <Box
                        overflowY={isProductVariant ? 'auto' : undefined}
                        height={isProductVariant ? [null, MODAL_HEIGHT] : undefined}
                        padding={isProductVariant ? [4, 5] : undefined}
                        paddingTop={isProductVariant ? [4, 5] : undefined}
                    >
                        {!isLoaded ? (
                            <Loader
                                hasBg={false}
                                isInline={true}
                                isShown={true}
                                style={{
                                    height: LOADER_HEIGHT,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center'
                                }}
                            />
                        ) : (
                            <>
                                {currentLocation && (
                                    <ExperienceLocation
                                        showInitialUseMyLocation={true}
                                        dataAt={'change_store'}
                                        clearOnFocus={true}
                                        isShowSearchIcon={false}
                                        currentLocation={currentLocation}
                                        updateCurrentLocation={getStoresAndUpdateState}
                                    />
                                )}
                                {isProductVariant && (
                                    <Box
                                        display={['block', 'none']}
                                        marginY={4}
                                    >
                                        <ProductDetails product={currentProduct} />
                                        <Divider
                                            marginTop={4}
                                            marginX={modal.outdentX}
                                        />
                                    </Box>
                                )}
                                {storeList && (
                                    <StoreList
                                        stores={storeList}
                                        selectedStore={selectedStore}
                                        showStoreDetails={showStoreDetails}
                                        selectStore={selectStore}
                                        openModal={openModal}
                                        pickupInventory={pickupInventory}
                                        variant={variant}
                                    />
                                )}
                                {showNoStoreResults && (
                                    <NoStores
                                        countryMismatch={countryMismatch}
                                        currentLocation={currentLocation}
                                        close={close}
                                    />
                                )}
                            </>
                        )}
                    </Box>
                </Grid>
            </Modal.Body>
            {(isProductVariant || (storeList && !showNoStoreResults && isLoaded)) && (
                <Modal.Footer>
                    <Grid
                        columns={footerColumns}
                        marginLeft={isProductVariant ? [0, LEFT_COLUMN_WIDTH] : undefined}
                    >
                        {(options?.isUpperFunnel || showCancelButton) && (
                            <Button
                                variant='secondary'
                                onClick={close}
                                children={localization.cancel}
                            />
                        )}
                        <Button
                            variant='primary'
                            children={getOkButtonText()}
                            {...(!showStoreDetails && { 'data-at': Sephora.debug.dataAt('change_store_use_this_store_btn') })}
                            disabled={!selectedStore || (showStoreDetails && !(selectedStore.isRopisable || selectedStore.isBopisable))}
                            block={true}
                            onClick={handleChangeStore}
                        />
                    </Grid>
                </Modal.Footer>
            )}
        </Modal>
    ) : null;
}

export default withInnerModal(wrapFunctionalComponent(InventoryAwareStorePickerModal, 'InventoryAwareStorePickerModal'), FindInStoreMapModal);
